/**
 * ToneSelector Component
 * Configures tone, voice, perspective, and formality settings
 */

import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { MessageSquare, User, Briefcase } from 'lucide-react';
import type { ToneConfig, TonePreset, Perspective, Formality } from '@/hooks/useContentConfig';

interface ToneSelectorProps {
  value: ToneConfig;
  onChange: (value: ToneConfig) => void;
  sourceLabel?: string;
}

const TONE_PRESETS: { value: TonePreset; label: string; description: string }[] = [
  { value: 'professional', label: 'Professional', description: 'Business-like, conveys expertise and authority' },
  { value: 'casual', label: 'Casual', description: 'Approachable, friendly and relaxed' },
  { value: 'technical', label: 'Technical', description: 'Precise, uses industry terminology' },
  { value: 'friendly', label: 'Friendly', description: 'Warm, builds rapport and trust' },
  { value: 'authoritative', label: 'Authoritative', description: 'Confident, establishes expertise' },
  { value: 'conversational', label: 'Conversational', description: 'Like a helpful discussion' },
];

const PERSPECTIVES: { value: Perspective; label: string; example: string }[] = [
  { value: 'first-person-plural', label: 'We/Our', example: '"We believe in quality. Our products..."' },
  { value: 'second-person', label: 'You/Your', example: '"You deserve the best. Your satisfaction..."' },
  { value: 'third-person', label: 'They/The company', example: '"The company offers premium products..."' },
];

const FORMALITY_LEVELS: { value: Formality; label: string; description: string }[] = [
  { value: 'formal', label: 'Formal', description: 'Complete sentences, no contractions' },
  { value: 'neutral', label: 'Neutral', description: 'Balanced, contractions acceptable' },
  { value: 'informal', label: 'Informal', description: 'Relaxed, casual expressions' },
];

export function ToneSelector({ value, onChange, sourceLabel }: ToneSelectorProps) {
  const handlePresetChange = (preset: TonePreset) => {
    onChange({ ...value, preset, customVoice: undefined });
  };

  const handleCustomVoiceChange = (customVoice: string) => {
    onChange({ ...value, customVoice: customVoice || undefined, preset: customVoice ? undefined : value.preset });
  };

  const handlePerspectiveChange = (perspective: Perspective) => {
    onChange({ ...value, perspective });
  };

  const handleFormalityChange = (formality: Formality) => {
    onChange({ ...value, formality });
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-blue-500" />
            <CardTitle className="text-lg">Tone & Voice</CardTitle>
          </div>
          {sourceLabel && (
            <Badge variant="outline" className="text-xs">
              From: {sourceLabel}
            </Badge>
          )}
        </div>
        <CardDescription>
          Configure how content sounds - the overall mood and writing style
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Tone Preset */}
        <div className="space-y-3">
          <Label className="text-sm font-medium">Tone Preset</Label>
          <RadioGroup
            value={value.preset || ''}
            onValueChange={(v) => handlePresetChange(v as TonePreset)}
            className="grid grid-cols-2 md:grid-cols-3 gap-3"
          >
            {TONE_PRESETS.map((preset) => (
              <div key={preset.value}>
                <RadioGroupItem
                  value={preset.value}
                  id={`tone-${preset.value}`}
                  className="peer sr-only"
                />
                <Label
                  htmlFor={`tone-${preset.value}`}
                  className="flex flex-col items-start gap-1 rounded-md border-2 border-muted bg-popover p-3 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary cursor-pointer"
                >
                  <span className="font-medium">{preset.label}</span>
                  <span className="text-xs text-muted-foreground">{preset.description}</span>
                </Label>
              </div>
            ))}
          </RadioGroup>
        </div>

        {/* Custom Voice */}
        <div className="space-y-2">
          <Label htmlFor="customVoice" className="text-sm font-medium">
            Custom Voice Guidelines (Optional)
          </Label>
          <Textarea
            id="customVoice"
            placeholder="Describe your brand voice in detail... e.g., 'Write like a knowledgeable friend who happens to be an expert cyclist. Use enthusiasm without being pushy.'"
            value={value.customVoice || ''}
            onChange={(e) => handleCustomVoiceChange(e.target.value)}
            className="min-h-[80px]"
            maxLength={500}
          />
          <p className="text-xs text-muted-foreground">
            {value.customVoice?.length || 0}/500 characters. Custom voice overrides tone presets.
          </p>
        </div>

        {/* Perspective */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-muted-foreground" />
            <Label className="text-sm font-medium">Perspective</Label>
          </div>
          <RadioGroup
            value={value.perspective}
            onValueChange={(v) => handlePerspectiveChange(v as Perspective)}
            className="grid grid-cols-1 md:grid-cols-3 gap-3"
          >
            {PERSPECTIVES.map((p) => (
              <div key={p.value}>
                <RadioGroupItem
                  value={p.value}
                  id={`perspective-${p.value}`}
                  className="peer sr-only"
                />
                <Label
                  htmlFor={`perspective-${p.value}`}
                  className="flex flex-col items-start gap-1 rounded-md border-2 border-muted bg-popover p-3 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary cursor-pointer"
                >
                  <span className="font-medium">{p.label}</span>
                  <span className="text-xs text-muted-foreground italic">{p.example}</span>
                </Label>
              </div>
            ))}
          </RadioGroup>
        </div>

        {/* Formality */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Briefcase className="h-4 w-4 text-muted-foreground" />
            <Label className="text-sm font-medium">Formality Level</Label>
          </div>
          <RadioGroup
            value={value.formality}
            onValueChange={(v) => handleFormalityChange(v as Formality)}
            className="grid grid-cols-3 gap-3"
          >
            {FORMALITY_LEVELS.map((f) => (
              <div key={f.value}>
                <RadioGroupItem
                  value={f.value}
                  id={`formality-${f.value}`}
                  className="peer sr-only"
                />
                <Label
                  htmlFor={`formality-${f.value}`}
                  className="flex flex-col items-center gap-1 rounded-md border-2 border-muted bg-popover p-3 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary cursor-pointer text-center"
                >
                  <span className="font-medium">{f.label}</span>
                  <span className="text-xs text-muted-foreground">{f.description}</span>
                </Label>
              </div>
            ))}
          </RadioGroup>
        </div>
      </CardContent>
    </Card>
  );
}
